import { useGetSettingsFooter } from '@agos/api-client/platform'
import { useEffect, useRef } from 'react'

import {
  CONSENT_EVENT,
  CONSENT_KEY,
  consentFrom,
  permittedTrackers,
  readConsent,
  trackerSettingsOf,
  type CookieConsent,
  type Tracker,
  type TrackerSettings,
} from './consent'
import {
  categoriesToForget,
  finishWithdrawal,
  forgetTrackerCookies,
  gaOptOutKey,
  refusedTrackers,
  rememberWithdrawal,
} from './withdrawal'

/**
 * The scripts a decision permits, injected once each.
 *
 * Separate from the banner so the banner file exports only a component - and because these two do
 * different jobs: one asks, the other obeys.
 *
 * **What is deliberately not ported: `customHeaderCode` and `customFooterCode`.** Legacy injects those
 * two admin-authored fields with `insertAdjacentHTML` into `<head>` and `<body>`, unsandboxed, with full
 * access to the customer's session - the opposite treatment to `CmsPage`, which frames admin HTML at a
 * null origin precisely so it cannot reach the token. Both are **empty** on this data, so nothing is
 * lost by leaving them out, and putting them back is a decision about who is trusted rather than a line
 * of code. Recorded in `docs/DECISIONS.md`.
 *
 * **Obeying includes taking back - FRONTEND.md §9.738, corrected after review.** A withdrawal is this side's job
 * rather than the card's, because only this side knows what the page runs: it compares every decision - this page's
 * own, and another tab's, which arrives as a `storage` event - with the one it last obeyed and with the trackers it
 * injected. A refused tracker the page runs is stopped with its own switch, the cookies of every category taken back
 * are deleted, and the page reloads; a category taken back whose tracker never loaded here has its cookies deleted and
 * nothing else. See `withdrawal.ts` for the rules.
 */

/** The id a tracker goes in under - the one place each is read, so injecting and stopping cannot disagree. */
function trackerId(tracker: Tracker, settings: TrackerSettings): string {
  if (tracker === 'google-analytics') return settings.googleAnalyticsId.trim()
  if (tracker === 'meta-pixel') return settings.metaPixelId.trim()
  return settings.tiktokPixelId.trim()
}

function injectTracker(tracker: Tracker, id: string) {
  if (typeof document === 'undefined') return

  if (tracker === 'google-analytics') {
    const loader = document.createElement('script')
    loader.async = true
    loader.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`
    document.head.appendChild(loader)
    const boot = document.createElement('script')
    // `anonymize_ip` is legacy's, and it stays: it is the one setting here that reduces what leaves.
    boot.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config',${JSON.stringify(id)},{anonymize_ip:true});`
    document.head.appendChild(boot)
    return
  }

  if (tracker === 'meta-pixel') {
    const script = document.createElement('script')
    script.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init',${JSON.stringify(id)});fbq('track','PageView');`
    document.head.appendChild(script)
    return
  }

  const script = document.createElement('script')
  script.text = `!function(w,d,t){w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};ttq.load(${JSON.stringify(id)});ttq.page();}(window,document,'ttq');`
  document.head.appendChild(script)
}

/**
 * Stops a tracker that is already running, with the tracker's own switch, before its cookies are deleted.
 *
 * A script cannot be unloaded and the reload that follows a withdrawal is not instant: GA4 raises `user_engagement`
 * on `pagehide`, which re-stamps `_ga_<id>` after the deletion and sends the withdrawn client id - measured by the
 * review against Google's own runtime. Each vendor's documented switch closes that window:
 *
 * - Google Analytics: `window['ga-disable-<id>'] = true`, which gtag checks on every event.
 * - The Meta pixel: `fbq('consent', 'revoke')`, which holds every event after it.
 * - TikTok: `ttq.disableCookie()`, the one switch this snippet's stub carries. TikTok is not configured on the
 *   owner's data, and the reload is what stops it.
 */
function stopTracker(tracker: Tracker, id: string): void {
  if (typeof window === 'undefined') return
  const page = window as unknown as Record<string, unknown>

  if (tracker === 'google-analytics') {
    if (id !== '') page[gaOptOutKey(id)] = true
    return
  }

  if (tracker === 'meta-pixel') {
    const fbq = page.fbq
    if (typeof fbq === 'function') (fbq as (...args: unknown[]) => void)('consent', 'revoke')
    return
  }

  const ttq = page.ttq as { disableCookie?: unknown } | undefined
  if (typeof ttq?.disableCookie === 'function') (ttq.disableCookie as () => void)()
}

function reloadPage() {
  window.location.reload()
}

/**
 * Loads whatever the current decision permits, once each, again when the decision changes, and takes back what a
 * decision refuses.
 *
 * `reload` is a parameter only so a test can watch it happen; the shell mounts this with the default.
 */
export function useConsentedTrackers({ reload = reloadPage }: { reload?: () => void } = {}) {
  const { data } = useGetSettingsFooter()
  // What this page has injected, with the id each went in under. Nothing leaves this map: a script cannot be
  // unloaded, so a tracker injected is a tracker the page runs until it goes.
  const loaded = useRef(new Map<Tracker, string>())
  // The decision this page last obeyed. What a decision takes back is measured against it, not against the stored
  // record, which another tab may already have changed.
  const obeyed = useRef<CookieConsent | null>(null)

  // Once per page load: what a withdrawal on the page before this one began, finished. Nothing refused is loaded
  // here, so the cookies deleted now stay deleted - see `finishWithdrawal`.
  useEffect(() => {
    finishWithdrawal(readConsent())
  }, [])

  useEffect(() => {
    // The settings are needed only to inject. Taking back needs nothing from them, so a customer who withdraws before
    // the footer settings answer - or when they never do - still has the cookies deleted.
    const settings = data === undefined ? null : trackerSettingsOf(data)

    function obey(decision: CookieConsent | null) {
      const before = obeyed.current
      obeyed.current = decision
      const refused = refusedTrackers(loaded.current.keys(), decision)
      const forget = categoriesToForget(before, decision, refused)
      // Stopped first, so nothing a tracker does while the page unloads writes a cookie back.
      for (const tracker of refused) stopTracker(tracker, loaded.current.get(tracker) ?? '')
      if (forget.length > 0) forgetTrackerCookies(forget)
      if (refused.length > 0) {
        rememberWithdrawal(forget)
        reload()
        return
      }

      if (settings === null) return
      // The stored record, as it has always been read here: when storage refused the write, nothing new is injected,
      // which is the safe direction to fail in.
      for (const tracker of permittedTrackers(readConsent(), settings)) {
        if (loaded.current.has(tracker)) continue
        const id = trackerId(tracker, settings)
        loaded.current.set(tracker, id)
        injectTracker(tracker, id)
      }
    }

    function onDecision(event: Event) {
      // What the customer has just decided, from the event itself: if storage refused the write, the page still stops
      // what they took back, even though it cannot remember it.
      obey(consentFrom((event as CustomEvent<unknown>).detail) ?? readConsent())
    }

    function onAnotherTab(event: StorageEvent) {
      // Another tab of this site wrote the decision. This one hears it only through `storage`, which does not fire in
      // the tab that wrote it, so no decision is obeyed twice.
      if (event.key !== CONSENT_KEY) return
      obey(readConsent())
    }

    // Already decided in a previous session, or decided just now.
    obey(readConsent())
    window.addEventListener(CONSENT_EVENT, onDecision)
    window.addEventListener('storage', onAnotherTab)
    return () => {
      window.removeEventListener(CONSENT_EVENT, onDecision)
      window.removeEventListener('storage', onAnotherTab)
    }
  }, [data, reload])
}
